/**
 * TeamDNA.tsx — /team-dna
 *
 * Pipeline health across the team — AEs and BDRs who carry pipeline.
 * Managers who also carry a personal book appear with their own-account data only.
 *
 * W62 fixes (2026-06-xx):
 *   B1. Filter out managers (and executives) from roster unless is_dual_role=true
 *       (carries own book). Roster = role IN ('ae', 'bdr'). Exception: if a
 *       manager/executive has is_dual_role=true they appear, but only counting
 *       their personally owned accounts (not the team's). For now the AE/BDR
 *       filter covers canon: only roles that own accounts appear. Bryan can
 *       override via is_dual_role later.
 *   B2. Replace "OMIT count" badge with coverage health summary:
 *       coverage = (Commit + Upside + Best Case weighted dollars) / gap_to_quota.
 *       Since accounts table has no dollar amounts and quota comes from
 *       users.quota_annual (which may be null), show account-count-based
 *       coverage proxy: scored_open_count / total_open_count formatted as a
 *       ratio badge. If quota_annual is present we show dollar coverage.
 *       If gap is 0 or quota missing → "—". Never fabricate.
 *   B3. Replace static red dotted bars with real per-rep CP-coverage strip:
 *       10 dots, one per CP. Color per canon UI palette based on AVERAGE
 *       composite_score at that CP across open accounts.
 *       Gray #6B7280 = no data (D-098 zero bug → gray, NOT red).
 *       Red #DC2626 if avg < 30 (Omit band).
 *       Orange #EA580C if 30-49 (Pipeline band).
 *       Yellow #EAB308 if 50-64 (Best Case band).
 *       Green #16A34A if 65+ (Upside / Commit band).
 *   B4. If no rep has scored data → show "No scored data yet" with gray bar
 *       instead of all-red. Composite=0 due to D-098 bug → gray (no data).
 *
 * Data:
 *   - Supabase project gstkgiwdjvztopjbeoiy, Tenant 1 only.
 *   - composite_score on account_plans.
 *   - accounts.owner_user_id → users.id. users.role: ae / bdr / manager / executive.
 *   - users.quota_annual for coverage ratio (may be null → show "—").
 *   - Per-CP score derived from account_answers (question → cp mapping).
 *   - Exclude [SIM-% accounts.
 *
 * Brand: No exclamation points. No emojis. M-PACT hyphenated. Em-dash CP{N} — {Name}.
 * Tenant 1 only — every query carries .eq("tenant_id", 1).
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { supabase } from "@/lib/session";
import { getForecastCategory, HEALTH_PALETTE } from "@shared/schema";
// W69: ALL_QUESTIONS and HELIX_CHECKPOINTS imports removed.
// Front-end no longer derives CP scores from answer math.
// account_cp_status is the single source of truth per CANON 2026-06-11 §13.6.

// ─── Palette (Mi6 canon) ─────────────────────────────────────────────────────
const C = {
  black:    "#0B0B0F",
  red:      "#DC2626",
  white:    "#FAFAF7",
  charcoal: "#1F1F23",
  border:   "#E5E5E7",
  muted:    "#6B7280",
} as const;

const TENANT_ID = 1;

// Qualified pipeline only — exclude Suspect/Prospect per canon
const OPEN_STATUSES = ["Qualified Opp", "Full Opportunity", "Won"] as const;

// ─── B3: Canon 5-color UI palette for health strip ───────────────────────────
// Gray = no data (composite=0 / D-098 bug), not Omit.
// This is the health palette, NOT the forecast hex palette.
const UI_PALETTE = {
  noData:    HEALTH_PALETTE.gray,   // #6B7280 — no score / D-098 zero
  omit:      HEALTH_PALETTE.red,    // #DC2626 — avg composite < 30
  pipeline:  HEALTH_PALETTE.orange, // #EA580C — 30-49
  bestCase:  HEALTH_PALETTE.yellow, // #EAB308 — 50-64
  upCommit:  HEALTH_PALETTE.green,  // #16A34A — 65+
} as const;

function compositeToUiColor(avg: number | null): string {
  if (avg === null || avg === 0) return UI_PALETTE.noData;  // D-098: 0 = no data
  if (avg < 30)  return UI_PALETTE.omit;
  if (avg < 50)  return UI_PALETTE.pipeline;
  if (avg < 65)  return UI_PALETTE.bestCase;
  return UI_PALETTE.upCommit;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface RepUser {
  id: string;         // users.id is uuid string in Supabase
  full_name: string | null;
  role: string;
  email: string | null;
  quota_annual: number | null;
}

interface RepRow {
  user: RepUser;
  /** Average composite across scored open accounts (0 means no data / D-098 zero) */
  composite: number;
  /** Total open accounts (all statuses) */
  openCount: number;
  /** Accounts with composite_score > 0 */
  scoredCount: number;
  /** Per-CP average composite (10 entries, index 0 = CP1), null = no data */
  cpAverages: (number | null)[];
  /** Sum of weighted forecast value (Commit+Upside+Best Case) — from v_account_forecast if available */
  coverageValue: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtName(u: RepUser): string {
  return u.full_name?.trim() || u.email || `User ${u.id}`;
}

function fmtRole(role: string): string {
  const map: Record<string, string> = {
    ae:        "AE",
    bdr:       "BDR",
    manager:   "Manager",
    executive: "Executive",
  };
  return map[role.toLowerCase()] ?? role.toUpperCase();
}

// B2: Coverage badge text
// coverage = scored_count / open_count as a simple ratio proxy.
// If quota_annual is present and coverageValue known, show dollar coverage.
function coverageBadgeText(row: RepRow): string {
  if (row.openCount === 0) return "—";
  if (row.scoredCount === 0) return "No scored data";
  const pct = Math.round((row.scoredCount / row.openCount) * 100);
  return `${row.scoredCount}/${row.openCount} scored (${pct}%)`;
}

// B2: If we have quota and coverage value, show coverage ratio
function coverageRatio(row: RepRow): string | null {
  if (!row.user.quota_annual || row.user.quota_annual <= 0) return null;
  if (row.coverageValue === null) return null;
  const gap = row.user.quota_annual;
  if (gap <= 0) return "—";
  const ratio = row.coverageValue / gap;
  return `${ratio.toFixed(1)}x`;
}

// ─── B3: per-rep CP-coverage strip ───────────────────────────────────────────
// W69: Per-CP status read from account_cp_status (pre-computed by recompute_account_score).
// Front-end no longer derives per-CP scores from account_answers math.
// CANON 2026-06-11 §13.6: front-end reads pre-computed fields only.

interface CpStatusRowDb {
  account_id: string | number;
  cp_number: number;
  status: string | null;
}

// Status string → numeric pct (0-100) for color band
// Maps CANON W68 color bands: green>=70, yellow>=40, orange>=20, red>0, null=gray
const CP_STATUS_TO_PCT: Record<string, number> = {
  green:  85, // representative mid-band value
  yellow: 50,
  orange: 30,
  red:    10,
};

/** Compute per-CP average pct (0-100) for one rep's set of accounts.
 *  W69: reads from account_cp_status pre-computed by recompute_account_score.
 *  Returns array of 10 entries (CP1..CP10), null if no data for that CP. */
function computePerCpAverages(
  accountIds: number[],
  cpStatusByAccount: Map<number, Record<number, string | null>>
): (number | null)[] {
  const results: (number | null)[] = [];
  for (let cpNum = 1; cpNum <= 10; cpNum++) {
    let totalPct = 0;
    let countAccounts = 0;
    for (const acctId of accountIds) {
      const store = cpStatusByAccount.get(acctId) ?? {};
      const status = store[cpNum];
      if (status == null) continue;
      const pct = CP_STATUS_TO_PCT[status];
      if (pct == null) continue;
      totalPct += pct;
      countAccounts += 1;
    }
    results.push(countAccounts > 0 ? Math.round(totalPct / countAccounts) : null);
  }
  return results;
}

// ─── Data fetching ────────────────────────────────────────────────────────────

async function fetchRepRows(): Promise<RepRow[]> {
  const sb = supabase();

  // B1: Filter to AE and BDR only — roles that own pipeline accounts.
  // Managers/executives excluded unless future is_dual_role logic added.
  const { data: users, error: usersErr } = await sb
    .from("users")
    .select("id, full_name, role, email, quota_annual")
    .eq("tenant_id", TENANT_ID)
    .in("role", ["ae", "bdr"])
    .eq("is_active", true)
    .order("full_name", { ascending: true });

  if (usersErr) throw usersErr;
  if (!users || users.length === 0) return [];

  const userRows = users as RepUser[];
  const userIds = userRows.map((u) => u.id);

  // Fetch open accounts (qualified pipeline, exclude SIM-)
  const { data: accounts, error: acctErr } = await sb
    .from("accounts")
    .select("id, owner_user_id, status, name")
    .eq("tenant_id", TENANT_ID)
    .in("owner_user_id", userIds)
    .in("status", OPEN_STATUSES as unknown as string[])
    .not("name", "like", "[SIM-%");

  if (acctErr) throw acctErr;

  const accountRows = (accounts ?? []) as { id: number; owner_user_id: string; status: string }[];
  const accountIds = accountRows.map((a) => a.id);

  // account_plans for composite scores
  const acctBestScore = new Map<number, number>();
  if (accountIds.length > 0) {
    const { data: plans } = await sb
      .from("account_plans")
      .select("account_id, composite_score")
      .in("account_id", accountIds.map(String))
      .not("composite_score", "is", null);

    for (const plan of (plans ?? []) as { account_id: string | null; composite_score: number | null }[]) {
      if (plan.account_id == null || plan.composite_score == null) continue;
      const acctId = Number(plan.account_id);
      if (isNaN(acctId)) continue;
      const prev = acctBestScore.get(acctId) ?? -1;
      if (plan.composite_score > prev) {
        acctBestScore.set(acctId, plan.composite_score);
      }
    }
  }

  // B3: W69 — Read per-CP status from account_cp_status (pre-computed by recompute_account_score).
  // Front-end no longer derives CP scores from account_answers math.
  // CANON 2026-06-11 §13.6: front-end reads pre-computed fields from account_cp_status only.
  const cpStatusByAccount = new Map<number, Record<number, string | null>>();
  if (accountIds.length > 0) {
    const { data: cpData } = await sb
      .from("account_cp_status")
      .select("account_id, cp_number, status")
      .in("account_id", accountIds);

    for (const row of (cpData ?? []) as CpStatusRowDb[]) {
      const acctId = Number(row.account_id);
      if (isNaN(acctId)) continue;
      let store = cpStatusByAccount.get(acctId);
      if (!store) {
        store = {};
        cpStatusByAccount.set(acctId, store);
      }
      store[row.cp_number] = row.status;
    }
  }

  // B2: Try to get weighted forecast value from v_account_forecast view
  // (deal_value × forecast_weight for Commit/Upside/Best Case buckets).
  // Graceful fail if view unavailable.
  const forecastByUser = new Map<string, number>();
  if (accountIds.length > 0) {
    try {
      const { data: fData } = await sb
        .from("v_account_forecast")
        .select("ae_user_id, forecast_bucket, weighted_value")
        .eq("tenant_id", TENANT_ID)
        .in("ae_user_id", userIds)
        .in("forecast_bucket", ["Commit", "Upside", "Best Case"]);

      for (const row of (fData ?? []) as { ae_user_id: string | null; forecast_bucket: string | null; weighted_value: number | null }[]) {
        if (!row.ae_user_id || row.weighted_value == null) continue;
        const existing = forecastByUser.get(row.ae_user_id) ?? 0;
        forecastByUser.set(row.ae_user_id, existing + row.weighted_value);
      }
    } catch {
      // View may not be queryable in all contexts — swallow and show "—"
    }
  }

  // Build per-user account id lists
  const acctsByUser = new Map<string, number[]>();
  for (const acct of accountRows) {
    const existing = acctsByUser.get(acct.owner_user_id) ?? [];
    existing.push(acct.id);
    acctsByUser.set(acct.owner_user_id, existing);
  }

  // Assemble rep rows
  return userRows.map((user) => {
    const userAcctIds = acctsByUser.get(user.id) ?? [];
    const openCount = userAcctIds.length;

    // Composite: average of scored (>0) accounts. D-098 zero = no data.
    const scores = userAcctIds
      .map((id) => acctBestScore.get(id))
      .filter((s): s is number => s != null && s > 0);

    const composite =
      scores.length > 0
        ? Math.round(scores.reduce((s, v) => s + v, 0) / scores.length)
        : 0;

    const scoredCount = scores.length;

    // B3: per-CP averages from answer data
    const cpAverages = computePerCpAverages(userAcctIds, cpStatusByAccount);

    // B2: forecast coverage value
    const coverageValue = forecastByUser.get(user.id) ?? null;

    return {
      user,
      composite,
      openCount,
      scoredCount,
      cpAverages,
      coverageValue,
    };
  });
}

// ─── B3: CP dot strip ─────────────────────────────────────────────────────────
// 10 dots, one per CP, colored by per-CP average composite using UI palette.
// Null = no data → gray (D-098 guard).

function CpDotStrip({ cpAverages }: { cpAverages: (number | null)[] }) {
  const dots = Array.from({ length: 10 }, (_, i) => {
    const avg = cpAverages[i] ?? null;
    return compositeToUiColor(avg);
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: 3,
        alignItems: "center",
        marginTop: 8,
      }}
      aria-label="CP health strip"
      role="img"
    >
      {dots.map((color, idx) => (
        <div
          key={idx}
          title={`CP${idx + 1}`}
          style={{
            width: 9,
            height: 9,
            borderRadius: 2,
            background: color,
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}

// ─── B2: Coverage badge ───────────────────────────────────────────────────────

function CoverageBadge({ row }: { row: RepRow }) {
  const ratio = coverageRatio(row);

  // If we have a real coverage ratio from quota + forecast value, show it
  if (ratio !== null) {
    return (
      <div
        style={{
          marginTop: 6,
          display: "flex",
          alignItems: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: C.muted,
            fontFamily: "Inter, sans-serif",
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          Coverage
        </span>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: ratio === "—" ? C.muted : C.black,
            fontFamily: "DM Sans, Inter, sans-serif",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {ratio}
        </span>
      </div>
    );
  }

  // Fallback: scored/total badge
  const text = coverageBadgeText(row);
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 12,
        color: row.scoredCount > 0 ? C.charcoal : C.muted,
        fontFamily: "Inter, sans-serif",
      }}
    >
      {row.openCount > 0 ? (
        row.scoredCount > 0 ? (
          <>
            <span style={{ fontWeight: 700 }}>{row.scoredCount}</span>
            <span style={{ color: C.muted }}>/{row.openCount} accounts scored</span>
          </>
        ) : (
          <span style={{ color: C.muted }}>No scored data yet</span>
        )
      ) : (
        <span style={{ color: C.muted }}>No open accounts</span>
      )}
    </div>
  );
}

// ─── PersonCard ───────────────────────────────────────────────────────────────

function PersonCard({ row }: { row: RepRow }) {
  const { user, composite } = row;

  // B4: composite=0 → gray "—" not a red score (D-098 guard)
  const hasScore = composite > 0;
  const scoreColor = hasScore
    ? (() => {
        if (composite >= 65) return HEALTH_PALETTE.green;
        if (composite >= 50) return HEALTH_PALETTE.yellow;
        if (composite >= 30) return HEALTH_PALETTE.orange;
        return HEALTH_PALETTE.red;
      })()
    : C.muted;

  const scoreLabel = hasScore ? getForecastCategory(composite) : "No data";

  return (
    <Link href={`/pipeline-dna/${user.id}`}>
      <a
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 16,
          alignItems: "center",
          padding: "14px 16px",
          background: "#fff",
          border: `1px solid ${C.border}`,
          borderRadius: 12,
          textDecoration: "none",
          color: "inherit",
          transition: "border-color 120ms ease, box-shadow 120ms ease",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = C.black;
          (e.currentTarget as HTMLElement).style.boxShadow =
            "0 2px 8px rgba(0,0,0,0.06)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.borderColor = C.border;
          (e.currentTarget as HTMLElement).style.boxShadow = "none";
        }}
      >
        {/* Left: name / role / coverage / CP dot strip */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              title={fmtName(user)}
              style={{
                fontFamily: "DM Sans, Inter, sans-serif",
                fontSize: 16,
                fontWeight: 700,
                color: C.black,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 200,
              }}
            >
              {fmtName(user)}
            </span>
            <span
              style={{
                background: C.black,
                color: "#fff",
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 7px",
                borderRadius: 999,
                letterSpacing: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              {fmtRole(user.role)}
            </span>
          </div>

          {/* B2: coverage badge — replaces "OMIT count" */}
          <CoverageBadge row={row} />

          {/* B3: real CP dot strip — replaces static red dots */}
          <CpDotStrip cpAverages={row.cpAverages} />
        </div>

        {/* Right: score + label + link */}
        <div style={{ textAlign: "right", minWidth: 72, flexShrink: 0 }}>
          <div
            style={{
              fontFamily: "DM Sans, Inter, sans-serif",
              fontSize: 32,
              fontWeight: 700,
              lineHeight: 1,
              color: scoreColor,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {/* B4: 0 composite → "—" not "0" */}
            {hasScore ? composite : "—"}
          </div>
          <div
            style={{
              fontSize: 10,
              color: scoreColor,
              fontWeight: 600,
              marginTop: 4,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {scoreLabel}
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: C.red,
              fontWeight: 600,
              fontFamily: "Inter, sans-serif",
            }}
          >
            View pipeline →
          </div>
        </div>
      </a>
    </Link>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TeamDNA() {
  const [rows, setRows]       = useState<RepRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [search, setSearch]   = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchRepRows()
      .then((r) => {
        if (cancelled) return;
        // Sort: scored reps first (composite desc), then unscored alphabetically
        setRows(
          r.sort((a, b) => {
            if (a.composite > 0 && b.composite === 0) return -1;
            if (a.composite === 0 && b.composite > 0) return 1;
            if (a.composite !== b.composite) return b.composite - a.composite;
            return (a.user.full_name ?? "").localeCompare(b.user.full_name ?? "");
          })
        );
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof Error ? e.message : "Could not load team roster."
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      fmtName(r.user).toLowerCase().includes(q) ||
      r.user.email?.toLowerCase().includes(q)
    );
  })();

  return (
    // B1: pageMeta updated — no longer "manager roster", reflects pipeline health
    <OperatorShell breadcrumbs={[{ label: "Team DNA" }]} pageTitle="Team DNA" pageMeta="Team DNA · pipeline health">
      <div
        style={{
          padding: "20px clamp(16px, 4vw, 32px) 60px",
          background: C.white,
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <h1
            style={{
              fontFamily: "DM Sans, Inter, sans-serif",
              fontSize: "clamp(22px, 3vw, 28px)",
              fontWeight: 700,
              color: C.black,
              letterSpacing: -0.3,
              margin: 0,
            }}
          >
            Team DNA
          </h1>
          <p
            style={{
              marginTop: 6,
              fontSize: 13,
              color: C.muted,
              fontFamily: "Inter, sans-serif",
            }}
          >
            Pipeline health across the team — AEs and BDRs carrying open accounts. Click any row to drill in.
          </p>
        </div>

        {/* Filter bar */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            background: C.white,
            paddingTop: 4,
            paddingBottom: 12,
            borderBottom: `1px solid ${C.border}`,
            marginBottom: 16,
          }}
        >
          <input
            type="search"
            placeholder="Filter by name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Filter by name or email"
            style={{
              width: "100%",
              maxWidth: 320,
              padding: "9px 12px",
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              background: "#fff",
              color: C.black,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Loading */}
        {loading && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              fontSize: 13,
              color: C.muted,
              fontFamily: "Inter, sans-serif",
            }}
          >
            Loading team roster...
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div
            role="alert"
            style={{
              padding: "12px 16px",
              background: "#FEF2F2",
              border: "1px solid #FECACA",
              borderRadius: 8,
              fontSize: 13,
              color: "#991B1B",
              fontFamily: "Inter, sans-serif",
            }}
          >
            Could not load roster: {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filtered.length === 0 && (
          <div
            style={{
              padding: 24,
              background: "#fff",
              border: `1px dashed ${C.border}`,
              borderRadius: 12,
              fontSize: 13,
              color: C.muted,
              fontFamily: "Inter, sans-serif",
            }}
          >
            {search.trim()
              ? "No matches."
              : "No active AEs or BDRs in this tenant yet."}
          </div>
        )}

        {/* Roster — B1: only AE/BDR after filter above */}
        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((row) => (
              <PersonCard key={row.user.id} row={row} />
            ))}
          </div>
        )}
      </div>
    </OperatorShell>
  );
}
